import {useColorScheme} from "../../../lib/color-scheme";
import {Button} from "../../../components/ui/button";
import {MoonStarIcon, SunIcon} from "lucide-react";
import {useEffect, useRef, useState} from "react";
import {cn} from "../../../lib/utils";
import {usePortfolioData} from "../data";

export const PAGE_TABS = ["cv", "projects", "certificates"] as const;
export type PageTabs = (typeof PAGE_TABS)[number];
export const PAGE_TABS_NAMES: Record<PageTabs, string> = {
    cv: "CV",
    projects: "Projects",
    certificates: "Certificates",
}

interface Props {
    tab: PageTabs;
    onTabSelected: (tab: PageTabs) => void
}

export function NavBar (props: Props) {
    const [isPinned, setIsPinned] = useState(false);
    const stickyElementRef = useRef<HTMLDivElement>(null);

    // on scroll effect to detect whether element is at top edge
    useEffect(() => {
        function onScroll () {
            if (!stickyElementRef.current) return;
            const atTop = stickyElementRef.current.getBoundingClientRect().top <= 0;
            if (atTop !== isPinned) {
                setIsPinned(atTop);
            }
        }
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    });

    return <>
        <div ref={stickyElementRef} className={"w-[100svw] h-16 flex flex-row items-center sticky top-0 backdrop-blur-lg bg-background/20 mb-8 z-30"}>
            <div className={"hidden md:block w-0 overflow-visible animate-in slide-in-from-left-2 fade-in duration-700"}>
                <NameLogo show={isPinned} />
            </div>
            <div className={"grow"}></div>
            <TabSwitcher extended={isPinned} tab={props.tab} onSelect={props.onTabSelected} />
            <div className={"grow"}></div>
        </div>
        <div className={"h-16 flex flex-row items-center justify-end px-4 absolute top-0 end-4"}>
            <ThemeToggleButton/>
        </div>
    </>
}

function NameLogo (props: { show: boolean }) {
    const { name } = usePortfolioData();
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }
    return <div onClick={scrollToTop} className={cn("w-32 opacity-45 font-semibold ps-4 cursor-pointer duration-500 transition-all", !props.show && "translate-x-[-20%] opacity-0")}>
        { name }
    </div>
}

function TabSwitcher(props: { tab: PageTabs; onSelect: (tab: PageTabs) => void, extended: boolean }) {
    const index = PAGE_TABS.indexOf(props.tab);

    return <div
        className={cn("animate-in slide-in-from-bottom-4 h-10 rounded-full relative outline outline-accent transition-all backdrop-blur-xl duration-500 outline-offset-2 w-[calc(min(90svw,256pt))]", props.extended && "w-[calc(min(90svw,320pt))]")}>
        <div className={"flex flex-row absolute top-0 start-0 end-0 bottom-0"}>
            <div style={{flexBasis: `calc(${100 * index / 3}% + ${index * 3}pt)`}}
                 className={"transition-all duration-300"}></div>
            <div className={"bg-secondary rounded-full basis-[calc(33%-6pt)]"}></div>
        </div>
        <div className={"grid grid-cols-3 absolute top-0 start-0 end-0 bottom-0 gap-[6pt]"}>
            { PAGE_TABS.map((t) => (
                <TabButton key={t} selected={props.tab === t} onSelect={() => props.onSelect(t)}>{ PAGE_TABS_NAMES[t] }</TabButton>
            ))}
        </div>
    </div>
}

function TabButton({ selected, children, onSelect }: React.PropsWithChildren<{
    selected: boolean,
    onSelect: () => void
}>) {
    return <Button
        variant={"ghost"}
        className={cn("h-full rounded-full text-foreground/80 hover:text-foreground hover:bg-foreground/5 hover:italic", selected && "!text-secondary-foreground !bg-transparent italic")}
        onClick={onSelect}
    >
        {children}
    </Button>
}

function ThemeToggleButton() {
    const {isDark, setIsDark} = useColorScheme();
    const toggleTheme = () => setIsDark(!isDark);

    return <Button onClick={toggleTheme} variant={"ghost"} className={"aspect-square animate-in slide-in-from-right-2 duration-700 z-50"}>
        {isDark ? <SunIcon className={"!w-5 !h-5"} /> : <MoonStarIcon className={"!w-5 !h-5"} /> }
    </Button>
}